import { Box, Center, VStack } from "@chakra-ui/react";
import { PageDetails_page_pageType } from "@the-volte/svc-core-sdk/lib/queries/gqlTypes/PageDetails";
import { ProductList_products_edges_node } from "@the-volte/svc-core-sdk/lib/queries/gqlTypes/ProductList";
import React, { useEffect } from "react";

import { ssrMode } from "@app/constants";
import { ProductDetails_lender } from "@app/views/Product/gqlTypes/ProductDetails";
import { Button, RichTextEditorContent } from "@components/atoms";
import { Filters } from "@components/legacy";
import {
  FilterSidebar,
  ProductList,
  ProductListHeader,
} from "@components/organisms";
import { Attribute } from "@graphql/gqlTypes/Attribute";
import { uniqueByKey } from "@utils/arrays";
import {
  DeliveryFilterType,
  LocationFilterType,
  PriceFilterType,
} from "@utils/filterData";

interface PageProps {
  title: string;
  textContent: string;
  aboveProductsDescription: string;
  attributes: Attribute[];
  activeFilters: number;
  activeSortOption: string | null;
  displayLoader: boolean;
  filters: Filters;
  hasNextPage: boolean;
  products: ProductList_products_edges_node[];
  numberOfProducts: number;
  clearFilters: () => void;
  onLoadMore: () => void;
  loadMoreError: boolean;
  onAttributeFiltersChange: (attributeSlug: string, value: string) => void;
  onOrder: (order: { value?: string; label: string }) => void;
  pricesFilter: PriceFilterType[];
  setPricesFilter: (obj: PriceFilterType[]) => void;
  locationFilter: LocationFilterType[];
  setLocationFilter: (obj: LocationFilterType) => void;
  disablePriceFiltering?: boolean;
  disableLocationFiltering?: boolean;
  lenderInfo?: ProductDetails_lender;
  deliveryFilter: DeliveryFilterType | undefined;
  setDeliveryFilter: (obj: DeliveryFilterType) => void;
  keyword?: string | undefined;
  setKeyword?: Function;
  pageType?: PageDetails_page_pageType;
  seoDescription?: string;
  hasCollectionSale?: boolean;
  isWarehouseSaleCollection?: boolean;
  warehouseSaleTitle?: string | null;
  isFetching?: boolean;
  aboveProductContent?: string;
}

export const ProductListPage: React.FC<PageProps> = ({
  title,
  textContent,
  seoDescription,
  aboveProductsDescription,
  attributes,
  activeFilters,
  activeSortOption,
  numberOfProducts,
  products,
  displayLoader,
  hasNextPage,
  clearFilters,
  onLoadMore,
  loadMoreError,
  filters,
  onOrder,
  onAttributeFiltersChange,
  pricesFilter,
  setPricesFilter,
  locationFilter,
  setLocationFilter,
  disablePriceFiltering = false,
  disableLocationFiltering = false,
  lenderInfo,
  deliveryFilter,
  setDeliveryFilter,
  keyword,
  setKeyword,
  pageType,
  hasCollectionSale,
  isWarehouseSaleCollection,
  warehouseSaleTitle,
  isFetching = false,
  aboveProductContent,
}) => {
  const [showFilters, setShowFilters] = React.useState(false);
  const [infiniteScrollingMode, setInfiniteScrollingMode] =
    React.useState<boolean>(false);
  products = uniqueByKey(products, "slug");
  const remainingProductCount = numberOfProducts - products.length;

  useEffect(() => {
    if (remainingProductCount <= 0) {
      setInfiniteScrollingMode(false);
    }
  }, [remainingProductCount]);

  if (
    isWarehouseSaleCollection &&
    !hasCollectionSale &&
    !displayLoader &&
    products.length
  ) {
    return (
      <VStack width="100%" p="0.5rem">
        <Box w="100%" textAlign="center" mt={4}>
          {warehouseSaleTitle} is not currently on, check back soon for our next
          big Warehouse Sale.
        </Box>
      </VStack>
    );
  }

  return (
    <VStack width="100%" p="0.5rem">
      <Box overflow="visible" width="100%">
        <ProductListHeader
          title={title}
          aboveProductsDescription={aboveProductsDescription}
          seoDescription={seoDescription}
          attributes={attributes}
          showFilters={showFilters}
          setShowFilters={setShowFilters}
          activeFilters={activeFilters}
          activeSortOption={activeSortOption}
          numberOfProducts={numberOfProducts}
          clearFilters={clearFilters}
          locationFilter={locationFilter}
          onLocationFilterChange={setLocationFilter}
          onPricesFilterChange={setPricesFilter}
          pricesFilter={pricesFilter}
          deliveryFilter={deliveryFilter}
          setDeliveryFilter={setDeliveryFilter}
          onOrder={onOrder}
          filters={filters}
          onAttributeFiltersChange={onAttributeFiltersChange}
          lenderInfo={lenderInfo}
          infiniteScrollingMode={infiniteScrollingMode}
          keyword={keyword}
          setKeyword={setKeyword}
          displayLoader={displayLoader}
          pageType={pageType}
          aboveProductContent={aboveProductContent}
        />
        <FilterSidebar
          show={showFilters}
          hide={() => setShowFilters(false)}
          onAttributeFiltersChange={onAttributeFiltersChange}
          attributes={attributes}
          filters={filters}
          pricesFilter={pricesFilter}
          setPricesFilter={setPricesFilter}
          locationFilter={locationFilter}
          setLocationFilter={setLocationFilter}
          disablePriceFiltering={disablePriceFiltering}
          disableLocationFiltering={disableLocationFiltering}
          deliveryFilter={deliveryFilter}
          setDeliveryFilter={setDeliveryFilter}
          keyword={keyword}
          setKeyword={setKeyword}
        />
        <Box width="100%">
          <ProductList
            products={products}
            numberOfProducts={numberOfProducts}
            canLoadMore={hasNextPage}
            loading={displayLoader}
            onLoadMore={onLoadMore}
            loadMoreError={loadMoreError}
            infiniteScrollingMode={infiniteScrollingMode}
            setInfiniteScrollingMode={setInfiniteScrollingMode}
            deliveryFilter={deliveryFilter}
            hasCollectionSale={hasCollectionSale}
            isWarehouseSaleCollection={isWarehouseSaleCollection}
            warehouseSaleTitle={warehouseSaleTitle}
            isFetching={isFetching}
          />
        </Box>
        {!infiniteScrollingMode && remainingProductCount > 0 && (
          <>
            {!ssrMode && (
              <Center padding={10}>
                <Button
                  width="300px"
                  testingContext="LoadMoreButton"
                  onClick={() => {
                    setInfiniteScrollingMode(true);
                    onLoadMore();
                  }}
                >{`Load more items (${remainingProductCount.toLocaleString()})`}</Button>
              </Center>
            )}
          </>
        )}
        <Center
          paddingLeft="6px"
          paddingRight="6px"
          maxWidth="1024px"
          marginX="auto"
          paddingTop="1.5rem"
          paddingBottom="1.5rem"
        >
          <RichTextEditorContent jsonData={textContent} />
        </Center>
      </Box>
    </VStack>
  );
};
